/*
 * Naomi Seoul mobile menu drawer: inline accordion sections.
 *
 * Dawn opens a drawer submenu as a second sliding panel and traps focus inside
 * it, with the hidden back button as the only way out. Rendered inline that
 * trap is a keyboard dead end, so the submenu summaries are handled here
 * instead of by MenuDrawer.onSummaryClick: one section open at a time, [open]
 * kept while the row collapses so the height transition can run, and focus
 * free to move on down the list.
 *
 * Dawn's `menu-opening` class contract is preserved: naomi-menu-drawer.css and
 * Dawn's Escape handler both read it, and closeMenuDrawer still clears it.
 */

// Must match the end of the closing collapse in naomi-menu-drawer.css (100ms delay + 300ms).
const NAOMI_DRAWER_COLLAPSE_MS = 400;

function naomiCollapseDrawerSection(details) {
  details.classList.remove('menu-opening');
  details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  clearTimeout(details.naomiCollapseTimer);
  details.naomiCollapseTimer = setTimeout(() => details.removeAttribute('open'), NAOMI_DRAWER_COLLAPSE_MS);
}

// Bring the tapped title to the top of the list as its children open. The
// row grows over 520ms, so a one-shot scrollTo would clamp to the height the
// list had at tap time; a short rAF glide chases the moving target instead,
// and the same glide absorbs a sibling section collapsing above it.
const NAOMI_DRAWER_GLIDE_MS = 700;

function naomiScrollDrawerSectionIntoPlace(summary) {
  const nav = summary.closest('.menu-drawer__navigation');
  if (!nav) return;
  // A nested row rises only as far as its section title's resting spot: aiming
  // the row itself at the top pushed the title up under the header.
  const title =
    summary.closest('.menu-drawer__navigation > ul > li')?.querySelector(':scope > details > summary') ?? summary;
  const padTop = parseFloat(getComputedStyle(nav).paddingTop) || 0;
  const offset = (el) => el.getBoundingClientRect().top - nav.getBoundingClientRect().top - padTop;
  // A nested group's open height, read from the clipped list's scrollHeight so
  // it is right from the first frame of the row's grow transition.
  const list = summary.parentElement.querySelector(':scope > .menu-drawer__submenu > .menu-drawer__menu');
  const target = () => {
    if (title === summary || !list) return Math.min(offset(summary), Math.max(offset(title), 0));
    // Scroll just enough to show the group's last link, never past the title's
    // resting spot unless title plus group overflow: then the group's own row
    // goes to the top. Never negative, so a list scrolled by hand stays put.
    const bottom = summary.getBoundingClientRect().top + summary.offsetHeight + list.scrollHeight;
    const want = bottom - nav.getBoundingClientRect().bottom;
    const limit = want > offset(title) ? offset(summary) : offset(title);
    return Math.min(Math.max(want, 0), Math.max(limit, 0));
  };
  const start = performance.now();
  cancelAnimationFrame(nav.naomiGlideFrame);
  const step = (now) => {
    nav.scrollTop += target() * 0.2;
    if (now - start < NAOMI_DRAWER_GLIDE_MS) nav.naomiGlideFrame = requestAnimationFrame(step);
  };
  nav.naomiGlideFrame = requestAnimationFrame(step);
}

// Dawn's --header-bottom-position reads -1px once the sticky header has been
// scrolled away, which makes the sheet overshoot the viewport. Size the sheet
// from its own top edge instead: once at open, and again after Dawn's header
// reveal transition has settled.
function naomiMeasureDrawerHeight() {
  const drawer = document.getElementById('menu-drawer');
  if (!drawer) return;
  const top = Math.max(0, Math.round(drawer.getBoundingClientRect().top));
  document.documentElement.style.setProperty('--n-drawer-height', `${window.innerHeight - top}px`);
}
function naomiMeasureDrawerTop() {
  requestAnimationFrame(naomiMeasureDrawerHeight);
  setTimeout(naomiMeasureDrawerHeight, 400);
}
window.addEventListener('resize', naomiMeasureDrawerHeight);

// One delegated listener on the document in the capture phase: it runs before
// Dawn's listener on the summary, and it survives the theme editor re-rendering
// the header section.
document.addEventListener(
  'click',
  (event) => {
    if (!event.target.closest) return;

    // The hamburger. Dawn's closeMenuDrawer strips open and menu-opening but
    // leaves aria-expanded true on the section summaries, so reset them here
    // rather than let a reopened drawer announce a collapsed row as expanded.
    if (event.target.closest('header-drawer > details > summary')) {
      document.querySelectorAll('.menu-drawer__navigation summary[aria-expanded="true"]').forEach((stale) => {
        stale.setAttribute('aria-expanded', 'false');
        clearTimeout(stale.parentElement.naomiCollapseTimer);
      });
      naomiMeasureDrawerTop();
      return;
    }

    const summary = event.target.closest(
      'header-drawer .menu-drawer__navigation summary.menu-drawer__menu-item'
    );
    if (!summary) return;
    // Above the drawer breakpoint Dawn's sliding panel is the right behaviour.
    if (!matchMedia('(max-width: 989px)').matches) return;

    event.preventDefault();
    event.stopPropagation();

    const details = summary.parentElement;
    const wasOpen = details.classList.contains('menu-opening');

    // Siblings only: a third-level summary must not collapse its own parent.
    summary
      .closest('ul.menu-drawer__menu')
      .querySelectorAll(':scope > li > details.menu-opening')
      .forEach(naomiCollapseDrawerSection);
    if (wasOpen) return;

    clearTimeout(details.naomiCollapseTimer);
    details.setAttribute('open', '');
    // Force the collapsed start state to be computed before transitioning.
    void details.offsetHeight;
    details.classList.add('menu-opening');
    summary.setAttribute('aria-expanded', 'true');
    naomiScrollDrawerSectionIntoPlace(summary);
  },
  true
);
